import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";
import { StockFixedIncomeCommittedPosition } from "../../model/domain/StockFixedIncomeModel";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { Contracted } from "../../model/domain/Contracted";
import {
  OperationType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { PublicSecurity } from "../../model/domain/PublicSecurity";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-compromissada-compra",
  templateUrl: "./compromissada-compra.component.html",
  styleUrls: ["./compromissada-compra.component.css"],
})
export class CompromissadaCompraComponent implements OnInit, OnDestroy {
  public model: Contracted = null;

  ListOperationTypesContracted: NamedType<number>[];

  navigationSubscription;

  canValidate = false;
  isEditing = false;

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  dataChanged: EventEmitter<string>;

  TicketTypeEnum = TicketType;

  publicSecurityTypes: string[];
  selectedPublicSecurityType: string = null;

  publicSecurityIdsList: string[] = null;
  selectedPublicSecurityId: string = null;

  publicSecurityListResult: PublicSecurity[] = null;
  publicSecurityList: PublicSecurity[] = null;
  selectedPublicSecurityItem: PublicSecurity = null;

  constructor(
    private service: TicketService,
    public validator: ValidatorService,
    private dialogService: DialogService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators: { [x: string]: (value: any) => boolean } = {
    operationTypeId: (value) =>
      [
        OperationType.Application,
        OperationType.Redemption,
        OperationType.FullRedemption,
      ].includes(value),
    valueOutward: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    valueReturn: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    unitPriceOutward: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    unitPriceReturn: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    operationValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.operationValueMask, {}),
    returnDate: (value) => this.model.returnDate != null && value != "",
    expirationDate: (value) => this.model.expirationDate != null && value != "",
    security: (value) => this.coalesce(value).length > 0,
    securityId: (value) => this.coalesce(value).length > 0,
  };

  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();
    this.service.getPublicSecurityTypes().subscribe((p) => {
      this.publicSecurityTypes = p;
    });
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new Contracted();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;
    this.model.id = 0;
    this.model.command = "0";
    this.model.operationTypeId = OperationType.Application;

    this.model.operationDate = new Date();
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(enabled);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null) {
    this.model = new Contracted();

    this.ListOperationTypesContracted =
      this.service.ListContractedOperationTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.operationTypeId = OperationType.Application;
    this.model.command = "0";

    if (id) {
      this.service.Contracted.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );

          if (this.model.counterpartDocument) {
            this.model.counterpartDocument = this.validator.formatDocument(
              this.model.counterpartDocument
            );
          }

          this.model.valueOutward = this.decimalPipe.transform(
            this.model.valueOutward,
            "1.0-8",
            "pt-BR"
          ) as any;
          this.model.valueReturn = this.decimalPipe.transform(
            this.model.valueReturn,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.unitPriceOutward = this.decimalPipe.transform(
            this.model.unitPriceOutward,
            "1.0-8",
            "pt-BR"
          ) as any;
          this.model.unitPriceReturn = this.decimalPipe.transform(
            this.model.unitPriceReturn,
            "1.0-8",
            "pt-BR"
          ) as any;

          if (this.model.operationValue) {
            this.model.operationValue = this.decimalPipe.transform(
              this.model.operationValue,
              "1.0-8",
              "pt-BR"
            ) as any;
          }

          if (this.model.issueTax) {
            this.model.issueTax = this.decimalPipe.transform(
              this.model.issueTax,
              "1.0-8",
              "pt-BR"
            ) as any;
          }

          this.model.returnDate = this.datePipe.transform(
            this.model.returnDate,
            "yyyy-MM-dd",
            "UTC"
          ) as any;

          this.model.expirationDate = this.datePipe.transform(
            this.model.expirationDate,
            "dd/MM/yyyy"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.selectedPublicSecurityType = this.model.security;

          this.service
            .getPublicSecurity(this.selectedPublicSecurityType)
            .subscribe((res) => {
              this.publicSecurityList = res;

              this.publicSecurityList.forEach((x) => {
                x.expirationDate = this.datePipe.transform(
                  x.expirationDate,
                  "dd/MM/yyyy",
                  "UTC/GMT"
                ) as any;
                x.issueDate = this.datePipe.transform(
                  x.issueDate,
                  "dd/MM/yyyy"
                ) as any;
              });

              this.selectedPublicSecurityItem = this.publicSecurityList.filter(
                (p) => p.expirationDate === this.model.expirationDate
              )[0];
            });

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Contracted])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.valueOutward = this.validator.getCurrency(data.valueOutward);
    data.valueReturn = this.validator.getCurrency(data.valueReturn);
    data.unitPriceOutward = this.validator.getCurrency(data.unitPriceOutward);
    data.unitPriceReturn = this.validator.getCurrency(data.unitPriceReturn);
    data.amount = this.validator.getCurrency(data.amount);
    data.operationValue = this.validator.getCurrency(data.operationValue);
    data.issueTax = this.validator.getNumeric(data.issueTax);
    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    data.returnDate = new Date(
      data.returnDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );
    data.expirationDate = new Date(
      data.expirationDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.Contracted.updateTicket(this.model.id, data)
        : this.service.Contracted.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((contracted: Contracted) => {
          if (!editing) {
            this.model.id = contracted.id;
            this.model.operationDate = contracted.operationDate;
            this.model.statusId = contracted.statusId;
            this.model.accountManagerName = contracted.accountManagerName;
            this.model.accountManagerDocument =
              contracted.accountManagerDocument;
            this.model.ticketCutoffTypeId = contracted.ticketCutoffTypeId;
            this.model.portfolioFundId = contracted.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        "A boleta não poderá mais ser editada, deseja continuar?"
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (contracted: Contracted) => {
            this.service.Contracted.forwardTicket(contracted.id).subscribe(
              (res) => {
                this.model.statusDescription = res.statusDescription;

                this.setEnable(false);

                this.snackBarService.open("Boleta encaminhada.", "OK", {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                });

                this.getTicket(contracted.id);
              }
            );
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Contracted.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject() {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { boletaId: TicketType.Contracted },
          ]);
        });
    });
  }

  isFullRedemption(): boolean {
    return this.model.operationTypeId === OperationType.FullRedemption;
  }

  isRedemption(): boolean {
    return this.model.operationTypeId === OperationType.Redemption;
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  isSell() {
    return this.model.operationTypeId === OperationType.Redemption;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Contracted;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  publicSecurityTypeSelected() {
    if (this.selectedPublicSecurityType) {
      this.model.security = this.selectedPublicSecurityType;

      this.clearPublicSecurityItem();

      this.service
        .getPublicSecurity(this.selectedPublicSecurityType)
        .subscribe((res) => {
          this.publicSecurityList = this.publicSecurityListResult = res;

          const securityIdList = res
            .map((item) => item.securityId)
            .filter((value, index, self) => self.indexOf(value) === index);

          this.publicSecurityIdsList = securityIdList;

          if (securityIdList.length > 1) {
            this.model.securityId = null;
          } else if (securityIdList.length === 1) {
            this.model.securityId = securityIdList[0];
          }

          this.publicSecurityList.forEach((x) => {
            x.expirationDate = this.datePipe.transform(
              x.expirationDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
            x.issueDate = this.datePipe.transform(
              x.issueDate,
              "dd/MM/yyyy"
            ) as any;
          });
        });
    } else {
      this.model.security = null;
      this.clearPublicSecurityItem();
    }
  }

  getStockSelectedFixedIncomeComitted(
    selectedFixedIncomeComitted: StockFixedIncomeCommittedPosition
  ) {
    if (this.isRedemption() || this.isFullRedemption()) {
      this.model.amount = selectedFixedIncomeComitted.quantity;
      this.model.operationValue = this.decimalPipe.transform(
        selectedFixedIncomeComitted.purchaseValue,
        "1.0-2",
        "pt-BR"
      ) as any;
      this.model.unitPriceOutward = this.decimalPipe.transform(
        selectedFixedIncomeComitted.pu,
        "1.0-8",
        "pt-BR"
      ) as any;
    }
  }
  publicSecurityIdSelected() {
    this.clearPublicSecurityItem();

    const filteredPublicSecurityList = this.publicSecurityListResult.filter(
      (x) => x.securityId === this.model.securityId
    );
    this.publicSecurityList = filteredPublicSecurityList;

    if (filteredPublicSecurityList.length === 1) {
      this.selectedPublicSecurityItem = filteredPublicSecurityList[0];
      this.publicSecurityItemSelected();
    }
  }

  publicSecurityItemSelected() {
    if (this.selectedPublicSecurityItem) {
      // this.model.securitySourceId = this.selectedPublicSecurityItem.securitySourceId;
      this.model.expirationDate =
        this.selectedPublicSecurityItem.expirationDate;
      // this.model.issueDate = this.selectedPublicSecurityItem.issueDate;
    } else {
      this.clearPublicSecurityItem();
    }
  }

  clearPublicSecurityItem() {
    // this.model.securitySourceId = null;
    this.model.expirationDate = null;
    // this.model.issueDate = null;

    this.selectedPublicSecurityItem = null;
  }
}
